// frontend/src/pages/policy_reviews/ReviewsList.jsx
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

export default function ReviewsList() {
  const navigate = useNavigate();

  const [reviews, setReviews] = useState([]);
  const [filteredReviews, setFilteredReviews] = useState([]);

  const [policies, setPolicies] = useState([]);
  const [certifications, setCertifications] = useState([]);

  const [selectedCertification, setSelectedCertification] = useState("");
  const [selectedPolicy, setSelectedPolicy] = useState("");

  /* ---------------- AUTH ---------------- */
  function getAuthHeaders() {
    const token = localStorage.getItem("accessToken");
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  /* ---------------- LOAD ---------------- */
  useEffect(() => {
    fetchReviews();
    fetchPolicies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function fetchReviews() {
    try {
      const res = await axios.get("/api/policy-reviews/", {
        headers: getAuthHeaders(),
      });
      setReviews(res.data || []);
      setFilteredReviews(res.data || []);
    } catch {
      setReviews([]);
      setFilteredReviews([]);
    }
  }

  async function fetchPolicies() {
    try {
      const res = await axios.get("/api/policies/", {
        headers: getAuthHeaders(),
      });

      const policyData = Array.isArray(res.data) ? res.data : [];
      setPolicies(policyData);

      // 🔹 BUILD CERTIFICATIONS (SAME AS CreateReview.jsx)
      const certMap = {};
      policyData.forEach((p) => {
        if (p.certification && p.certification_name) {
          certMap[p.certification] = p.certification_name;
        }
      });

      const certList = Object.entries(certMap).map(([id, name]) => ({
        id,
        certification_name: name,
      }));

      setCertifications(certList);
    } catch {
      setPolicies([]);
      setCertifications([]);
    }
  }

  /* ---------------- FILTERS ---------------- */
  useEffect(() => {
    let data = [...reviews];

    if (selectedCertification) {
      const certPolicies = policies
        .filter(
          (p) => String(p.certification) === String(selectedCertification)
        )
        .map((p) => p.policy_name);

      data = data.filter((r) =>
        certPolicies.includes(r.policy_name)
      );
    }

    if (selectedPolicy) {
      data = data.filter(
        (r) => r.policy_name === selectedPolicy
      );
    }

    setFilteredReviews(data);
  }, [selectedCertification, selectedPolicy, reviews, policies]);

  /* ---------------- ACTIONS ---------------- */
  const submitReview = async (id) => {
    try {
      await axios.post(
        `/api/policy-reviews/${id}/submit/`,
        {},
        { headers: getAuthHeaders() }
      );
      fetchReviews();
    } catch {
      alert("Submit failed");
    }
  };

  /* ---------------- UI ---------------- */
  return (
    <div className="pageWrapper">
      <div className="headerRow">
        <h1 className="pageTitle">Previous Policy Reviews</h1>
        <button
          className="primaryBtn"
          onClick={() => navigate("/policy-reviews/new")}
        >
          + Start New Review
        </button>
      </div>

      {/* FILTERS */}
      <div className="filterRow">
        <select
          className="modernInput"
          value={selectedCertification}
          onChange={(e) => {
            setSelectedCertification(e.target.value);
            setSelectedPolicy("");
          }}
        >
          <option value="">-- All Certifications --</option>
          {certifications.map((c) => (
            <option key={c.id} value={c.id}>
              {c.certification_name}
            </option>
          ))}
        </select>

        <select
          className="modernInput"
          value={selectedPolicy}
          onChange={(e) => setSelectedPolicy(e.target.value)}
        >
          <option value="">-- All Policies --</option>
          {policies
            .filter(
              (p) =>
                !selectedCertification ||
                String(p.certification) === String(selectedCertification)
            )
            .map((p) => (
              <option key={p.id} value={p.policy_name}>
                {p.policy_name}
              </option>
            ))}
        </select>
      </div>

      {/* TABLE */}
      <div className="tableCard">
        <table className="modernTable">
          <thead>
            <tr>
              <th>Certification</th>
              <th>Policy</th>
              <th>Version</th>
              <th>Review Date</th>
              <th>Status</th>
              <th style={{ textAlign: "right" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredReviews.map((r) => (
              <tr key={r.id}>
                <td>
                  {
                    certifications.find((c) =>
                      policies.find(
                        (p) =>
                          p.policy_name === r.policy_name &&
                          String(p.certification) === String(c.id)
                      )
                    )?.certification_name || "-"
                  }
                </td>
                <td>{r.policy_name}</td>
                <td>{r.policy_version || "-"}</td>
                <td>{r.review_date || "-"}</td>
                <td>
                  <span className={`statusBadge status${r.status}`}>
                    {r.status}
                  </span>
                </td>
                <td style={{ textAlign: "right" }}>
                  <button
                    className="actionBtn"
                    onClick={() => navigate(`/policy-reviews/${r.id}`)}
                  >
                    View
                  </button>

                  {r.status === "draft" && (
                    <>
                      <button
                        className="actionBtn secondary"
                        onClick={() =>
                          navigate(`/policy-reviews/${r.id}/edit`)
                        }
                      >
                        Edit
                      </button>

                      <button
                        className="actionBtn danger"
                        onClick={() => submitReview(r.id)}
                      >
                        Submit
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}

            {filteredReviews.length === 0 && (
              <tr>
                <td colSpan="6" className="emptyRow">
                  No reviews found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* STYLES */}
      <style>{`
        .pageWrapper {
          padding: 40px;
          background: #f5f7fb;
          min-height: 100vh;
          font-family: Inter, sans-serif;
        }
        .headerRow {
          display: flex;
          justify-content: space-between;
          margin-bottom: 20px;
        }
        .pageTitle {
          font-size: 26px;
          font-weight: 700;
          color: #0f1d60;
        }
        .filterRow {
          display: flex;
          gap: 12px;
          margin-bottom: 20px;
        }
        .modernInput {
          padding: 10px;
          border-radius: 10px;
          border: 1px solid #d1d5db;
        }
        .tableCard {
          background: white;
          padding: 20px;
          border-radius: 16px;
          box-shadow: 0 8px 25px rgba(0,0,0,0.1);
        }
        .modernTable {
          width: 100%;
          border-collapse: collapse;
        }
        .modernTable th {
          background: #0f1d60;
          color: white;
          padding: 12px;
        }
        .modernTable td {
          padding: 12px;
          border-bottom: 1px solid #e5e7eb;
        }
        .emptyRow {
          text-align: center;
          padding: 20px;
        }
        .actionBtn {
          padding: 8px 12px;
          margin-left: 6px;
          border-radius: 8px;
          border: none;
          cursor: pointer;
        }
        .actionBtn.secondary {
          background: #dbeafe;
        }
        .actionBtn.danger {
          background: #fee2e2;
        }
      `}</style>
    </div>
  );
}
